import React, { useState, useMemo, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Input,
  Form,
  FormGroup,
  Label,
  CardTitle,
  Button,
  Card,
  CardBody,
  Alert,
  FormFeedback,
  Row,
  Col,
  Badge,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { createSurveyQuestion, createSurveyTitle } from "../../api/survey"
import { useDispatch, useSelector } from "react-redux"
import {
  getSurveyQuestions,
  setSurveyQuestionSuccess,
  setSurveySuccess,
} from "store/survey/actions"
import Swal from "sweetalert2"

const Survey = () => {
  const dispatch = useDispatch()
  document.title = "Create Surveys"

  const [surveyCreated, setSurveyCreated] = useState(false)
  const [surveyData, setSurveyData] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRef = useRef(null)

  const { surveyQuestions, survey } = useSelector(state => ({
    surveyQuestionsPages: state.Surveys.surveyQuestionsPages,
    totalQuestions: state.Surveys.totalQuestions,
    surveyQuestions: state.Surveys.surveyQuestions,
    surveyQuestion: state.Surveys.surveyQuestion,
    survey: state.Surveys.survey,
  }))

  const navigate = useNavigate()

  useEffect(() => {
    if (surveyCreated) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      })
    }
  }, [surveyCreated])

  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    type: "",
  })

  const [questionFormValues, setQuestionFormValues] = useState({
    text: "",
    type: "",
    options: ["", ""],
  })

  const [formErrors, setFormErrors] = useState({})

  // Simple validation
  const validateSurveyForm = () => {
    const errors = {}
    if (!formValues.title.trim()) errors.title = "Title is required"
    if (!formValues.description.trim())
      errors.description = "Description is required"
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const validateQuestionForm = () => {
    const errors = {}
    if (!questionFormValues.text.trim())
      errors.text = "Question text is required"
    if (!questionFormValues.type) errors.type = "Question type is required"

    if (questionFormValues.type !== "Open") {
      const validOptions = questionFormValues.options.filter(option =>
        option.trim()
      )
      if (validOptions.length < 2) {
        errors.options = "At least 2 options are required"
      }
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async event => {
    event.preventDefault()

    if (!validateSurveyForm()) return

    setIsSubmitting(true)

    try {
      const surveyData = {
        title: formValues.title.trim(),
        description: formValues.description.trim(),
      }

      const topicResponse = await createSurveyTitle(surveyData)
      dispatch(setSurveySuccess(topicResponse.data))

      setSurveyCreated(true)
      setSurveyData(topicResponse.data)

      setFormValues({ title: "", description: "", type: "" })
      setFormErrors({})
    } catch (error) {
      console.error("Error creating survey:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error creating survey. Please try again.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (survey?.id) {
      dispatch(getSurveyQuestions({ page: 0, surveyId: survey.id, size: 20 }))
    }
  }, [dispatch, survey])

  const handleQuestionSubmit = async event => {
    event.preventDefault()

    if (!validateQuestionForm()) return

    try {
      const validOptions = questionFormValues.options.filter(option =>
        option.trim()
      )

      const questionData = {
        text: questionFormValues.text.trim(),
        type: questionFormValues.type,
        surveyId: surveyData.id,
        options:
          questionFormValues.type === "Open"
            ? []
            : validOptions.map(title => ({
                Content: title.trim(),
                surveyId: surveyData.id,
              })),
      }

      const topicResponse = await createSurveyQuestion(questionData)
      dispatch(setSurveyQuestionSuccess(topicResponse.data))

      setQuestionFormValues({
        text: "",
        type: "",
        options: ["", ""],
      })
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Question added successfully.",
      })
      setFormErrors({})
    } catch (error) {
      console.error("Error creating question:", error)
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error creating question. Please try again.",
      })
    }
  }

  const handleAddOption = () => {
    setQuestionFormValues({
      ...questionFormValues,
      options: [...questionFormValues.options, ""],
    })
  }

  const handleDeleteOption = index => {
    if (questionFormValues.options.length <= 2) return

    const updatedOptions = [...questionFormValues.options]
    updatedOptions.splice(index, 1)
    setQuestionFormValues({
      ...questionFormValues,
      options: updatedOptions,
    })
  }

  const handleOptionTitleChange = (index, value) => {
    const updatedOptions = [...questionFormValues.options]
    updatedOptions[index] = value
    setQuestionFormValues({
      ...questionFormValues,
      options: updatedOptions,
    })
  }

  const handleInput = event => {
    event.target.style.height = "auto"
    event.target.style.height = `${event.target.scrollHeight}px`
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto"
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`
    }
  }, [formValues.description])

  const handleBack = () => {
    navigate("/surveys")
  }

  const handleCreateAnother = () => {
    window.location.reload()
  }

  const qcolumns = useMemo(
    () => [
      {
        Header: "#",
        accessor: "id",
        Cell: ({ row }) => <span>Q{row.index + 1}</span>,
      },
      {
        Header: "QUESTION",
        accessor: "text",
      },
      {
        Header: "TYPE",
        accessor: "type",
        Cell: ({ value }) => (
          <Badge color={value === "Open" ? "info" : "primary"}>
            {value === "Multiple"
              ? "Multiple Choice"
              : value === "Single"
              ? "Single Choice"
              : "Open"}
          </Badge>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between mb-4">
              <Breadcrumbs title="Surveys" breadcrumbItem="Create Survey" />
              <Button color="primary" onClick={handleBack}>
                <i className="bx bx-arrow-back me-1"></i> Back
              </Button>
            </div>

            {!surveyCreated ? (
              <Form onSubmit={handleSubmit}>
                <FormGroup className="mb-3">
                  <Label for="title">Survey Title</Label>
                  <Input
                    type="text"
                    id="title"
                    value={formValues.title}
                    onChange={e => {
                      setFormValues({ ...formValues, title: e.target.value })
                      if (formErrors.title)
                        setFormErrors({ ...formErrors, title: null })
                    }}
                    invalid={!!formErrors.title}
                    placeholder="Enter survey title"
                  />
                  {formErrors.title && (
                    <FormFeedback>{formErrors.title}</FormFeedback>
                  )}
                </FormGroup>

                <FormGroup className="mb-4">
                  <Label for="description">Description</Label>
                  <Input
                    type="textarea"
                    id="description"
                    innerRef={inputRef}
                    value={formValues.description}
                    onChange={e => {
                      setFormValues({
                        ...formValues,
                        description: e.target.value,
                      })
                      handleInput(e)
                      if (formErrors.description)
                        setFormErrors({ ...formErrors, description: null })
                    }}
                    invalid={!!formErrors.description}
                    style={{ overflow: "hidden", resize: "none" }}
                    placeholder="Describe your survey"
                  />
                  {formErrors.description && (
                    <FormFeedback>{formErrors.description}</FormFeedback>
                  )}
                </FormGroup>

                <div className="text-center">
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Creating..." : "Create Survey"}
                  </Button>
                </div>
              </Form>
            ) : (
              <div>
                {/* Survey Info */}
                <div className="bg-light p-3 rounded mb-4">
                  <h4 className="text-success mb-2">
                    <i className="bx bx-check-circle me-2"></i>
                    Survey Created Successfully
                  </h4>
                  <p className="mb-1">
                    <strong>Title:</strong> {surveyData?.title}
                  </p>
                  <p className="mb-0">
                    <strong>Description:</strong> {surveyData?.description}
                  </p>
                </div>

                {/* Question Form */}
                <h4 className="mb-3">Add Questions</h4>
                <Form onSubmit={handleQuestionSubmit}>
                  <Row>
                    <Col md={8}>
                      <FormGroup className="mb-3">
                        <Label for="text">Question</Label>
                        <Input
                          type="text"
                          id="text"
                          value={questionFormValues.text}
                          onChange={e => {
                            setQuestionFormValues({
                              ...questionFormValues,
                              text: e.target.value,
                            })
                            if (formErrors.text)
                              setFormErrors({ ...formErrors, text: null })
                          }}
                          invalid={!!formErrors.text}
                          placeholder="Enter your question"
                        />
                        {formErrors.text && (
                          <FormFeedback>{formErrors.text}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className="mb-3">
                        <Label for="type">Type *</Label>
                        <Input
                          type="select"
                          id="type"
                          value={questionFormValues.type}
                          onChange={e => {
                            setQuestionFormValues({
                              ...questionFormValues,
                              type: e.target.value,
                            })
                            if (formErrors.type)
                              setFormErrors({ ...formErrors, type: null })
                          }}
                          invalid={!!formErrors.type}
                        >
                          <option value="">Select type</option>
                          <option value="Open">Open</option>
                          <option value="Multiple">Multiple Choice</option>
                          <option value="Single">Single Choice</option>
                        </Input>
                        {formErrors.type && (
                          <FormFeedback>{formErrors.type}</FormFeedback>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Options */}
                  {questionFormValues.type &&
                    questionFormValues.type !== "Open" && (
                      <FormGroup className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <Label>Options</Label>
                          <Button
                            color="success"
                            size="sm"
                            type="button"
                            onClick={handleAddOption}
                          >
                            Add Option
                          </Button>
                        </div>

                        {questionFormValues.options.map((title, index) => (
                          <Row key={index} className="mb-2">
                            <Col md={10}>
                              <Input
                                type="text"
                                value={title}
                                onChange={e =>
                                  handleOptionTitleChange(index, e.target.value)
                                }
                                placeholder={`Option ${index + 1}`}
                                required
                              />
                            </Col>
                            <Col md={2}>
                              {questionFormValues.options.length > 2 && (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDeleteOption(index)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Col>
                          </Row>
                        ))}
                      </FormGroup>
                    )}

                  <div className="text-center mb-4">
                    <Button color="success" type="submit">
                      Add Question
                    </Button>
                  </div>
                </Form>

                <div className="text-center">
                  <Button color="outline-primary" onClick={handleCreateAnother}>
                    Create Another Survey
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Questions Table */}
        {surveyQuestions.length > 0 && (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h5">
                Survey Questions ({surveyQuestions.length})
              </CardTitle>
              <TableContainer
                columns={qcolumns}
                data={surveyQuestions || []}
                isGlobalFilter={true}
                isAddOptions={false}
                customPageSize={10}
                isPagination={true}
                iscustomPageSizeOptions={true}
                tableClass="align-middle table-nowrap table-check table"
                theadClass="table-light"
                paginationDiv="col-12"
                pagination="justify-content-left pagination pagination-rounded"
              />
            </CardBody>
          </Card>
        )}
      </div>
    </React.Fragment>
  )
}

export default Survey
